import { useCallback, useEffect, useRef } from "react";
import { useZero } from "@rocicorp/zero/react";
import { mutators } from "@/lib/mutators";

interface User {
  id: string;
  display_name: string;
}

interface UseTypingIndicatorProps {
  user: User | null;
  channelId: string;
}

interface UseTypingIndicatorReturn {
  setTyping: () => void;
  clearTyping: () => void;
  stopHeartbeat: () => void;
}

export function useMessageTypingIndicator({
  user,
  channelId,
}: UseTypingIndicatorProps): UseTypingIndicatorReturn {
  const zero = useZero();

  const typingChannelRef = useRef<string | null>(null);
  const typingHeartbeatRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const userRef = useRef(user);
  const zeroRef = useRef(zero);
  const channelIdRef = useRef(channelId);

  // Keep refs in sync with current values (outside of render)
  useEffect(() => {
    userRef.current = user;
    zeroRef.current = zero;
    channelIdRef.current = channelId;
  }, [user, zero, channelId]);

  const stopHeartbeat = useCallback(() => {
    if (typingHeartbeatRef.current) {
      clearInterval(typingHeartbeatRef.current);
      typingHeartbeatRef.current = null;
    }
  }, []);

  const clearTyping = useCallback(() => {
    stopHeartbeat();
    const u = userRef.current;
    if (!u) return;
    if (typingChannelRef.current) {
      typingChannelRef.current = null;
      zeroRef.current.mutate(
        mutators.user_presence.clearTyping({ user_id: u.id }),
      );
    }
  }, [stopHeartbeat]);

  const setTyping = useCallback(() => {
    const u = userRef.current;
    if (!u) return;
    const cid = channelIdRef.current;

    const updateTimestamp = () => {
      const currentUser = userRef.current;
      const currentChannelId = channelIdRef.current;
      if (!currentUser) return;
      // If channel changed out from under us, stop this heartbeat
      if (typingChannelRef.current !== currentChannelId) {
        stopHeartbeat();
        return;
      }
      zeroRef.current.mutate(
        mutators.user_presence.setTyping({
          user_id: currentUser.id,
          channel_id: currentChannelId,
        }),
      );
    };

    if (typingChannelRef.current !== cid) {
      // New channel or first keystroke — set typing and start heartbeat
      stopHeartbeat();
      typingChannelRef.current = cid;
      updateTimestamp();
      // 3500ms matches 70% of TypingIndicator's 5000ms staleness threshold —
      // leaves one retry worth of headroom while cutting heartbeat mutation
      // frequency by ~40% vs the prior 2000ms cadence.
      typingHeartbeatRef.current = setInterval(updateTimestamp, 3500);
    }
    // If already typing in same channel, heartbeat handles refresh — no action needed
  }, [stopHeartbeat]);

  // Clear typing on unmount or channel change
  useEffect(() => {
    return () => {
      stopHeartbeat();
      const u = userRef.current;
      if (typingChannelRef.current && u) {
        zeroRef.current.mutate(
          mutators.user_presence.clearTyping({ user_id: u.id }),
        );
        typingChannelRef.current = null;
      }
    };
  }, [channelId, stopHeartbeat]);

  return {
    setTyping,
    clearTyping,
    stopHeartbeat,
  };
}
